import { useCallback, useMemo, useState } from 'react';
import useLocalize from '../../utils/hooks/useLocalize';
import CriteriaCheckbox from './CriteriaCheckbox';
import RangeSelector from './RangeSelector';
import ScanRadioButton from './ScanRadioButton';

const ScanCriteriaSection = () => {
  const localize = useLocalize();
  const [selectedOption, setSelectedOption] = useState('option1');

  // Keeps the state for all criteria options
  const [selectedCriteria, setSelectedCriteria] = useState({
    AllSecurities: true
  });

  // Sample data for dividend yield range selector (配当利回り)
  // Generate sample data: values between 0-100% with realistic distribution
  const sampleDividendData = useMemo(() => {
    const data = [];

    // Generate a more realistic distribution for dividend yields
    // Most companies have yields between 0-10%, with fewer having higher yields
    for (let i = 0; i < 8000; i++) {
      let value;
      const rand = Math.random();

      if (rand < 0.4) {
        // 40% of companies: 0-3% yield (common range)
        value = Math.random() * 3;
      } else if (rand < 0.7) {
        // 30% of companies: 3-8% yield (moderate range)
        value = 3 + Math.random() * 5;
      } else if (rand < 0.9) {
        // 20% of companies: 8-15% yield (higher range)
        value = 8 + Math.random() * 7;
      } else {
        // 10% of companies: 15-100% yield (very high range, REITs, special situations)
        value = 15 + Math.random() * 85;
      }

      data.push(Math.round(value * 100) / 100); // Round to 2 decimal places
    }
    return data;
  }, []);

  // eslint-disable-next-line no-unused-vars
  const [dividendYieldRange, setDividendYieldRange] = useState({
    min: null,
    max: null
  });
  // eslint-disable-next-line no-unused-vars
  const [marketCapRange, setMarketCapRange] = useState({
    min: null,
    max: null
  });

  // Sample data for market cap (時価総額)
  // Generate sample data: values between 10-10000 billion yen
  const sampleMarketCapData = useMemo(() => {
    const data = [];
    // Box-Muller transform to generate normal distribution
    const generateNormal = (mean, stdDev) => {
      let u = 0,
        v = 0;
      while (u === 0) u = Math.random(); // Converting [0,1) to (0,1)
      while (v === 0) v = Math.random();
      const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
      return z * stdDev + mean;
    };

    for (let i = 0; i < 5000; i++) {
      // Normal distribution centered around 1000 billion yen with std dev of 500
      let value = generateNormal(1000, 500);
      // Clamp to reasonable range (10-10000 billion yen)
      value = Math.max(10, Math.min(10000, value));
      data.push(value);
    }
    return data;
  }, []);

  const handleDividendYieldChange = useCallback((range) => {
    setDividendYieldRange(range);
    console.log('Dividend Yield Range:', range);
  }, []);

  const handleMarketCapChange = useCallback((range) => {
    setMarketCapRange(range);
    console.log('Market Cap Range:', range);
  }, []);

  return (
    <div className="scan-criteria-section">
      <CriteriaCheckbox
        topText={localize('desktop.scans.criteria.securities.type')}
        bottomText={localize('desktop.scans.criteria.all')}
        isSelected={selectedCriteria.AllSecurities}
        onClick={() => {
          const newValue = !selectedCriteria.AllSecurities;
          setSelectedCriteria((prev) => ({
            ...prev,
            AllSecurities: newValue
          }));
        }}
      />

      <div
        className="scan-radio-button-group-container"
        style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}
      >
        <ScanRadioButton
          text="選択1"
          value="option1"
          name="scanOptions"
          isSelected={selectedOption === 'option1'}
          onClick={() => setSelectedOption('option1')}
        />
        <ScanRadioButton
          text="選択2"
          value="option2"
          name="scanOptions"
          isSelected={selectedOption === 'option2'}
          onClick={() => setSelectedOption('option2')}
        />
        <ScanRadioButton
          text="選択3"
          value="option3"
          name="scanOptions"
          isSelected={selectedOption === 'option3'}
          onClick={() => setSelectedOption('option3')}
        />
      </div>

      {/* Example 1: Dividend Yield Range Selector (配当利回り) */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '10px', color: '#333' }}>配当利回り</h4>
        <RangeSelector
          values={sampleDividendData}
          unit="%"
          onChange={handleDividendYieldChange}
        />
      </div>

      {/* Example 2: Market Cap Range Selector (時価総額) */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '10px', color: '#333' }}>時価総額</h4>
        <RangeSelector
          values={sampleMarketCapData}
          unit="億円"
          onChange={handleMarketCapChange}
        />
      </div>
      <div style={{ marginTop: '8px', color: '#333' }}>
        選択された範囲 — 配当利回り: {dividendYieldRange.min} 〜{' '}
        {dividendYieldRange.max} % ｜ 時価総額: {marketCapRange.min} 〜{' '}
        {marketCapRange.max} 億円
      </div>

      <div className="placeholder-text">
        {localize('desktop.scans.criteria.construction')}
      </div>
    </div>
  );
};

export default ScanCriteriaSection;
